import type { DialogHandle } from 'components/modal_manager';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { CustomTestApi } from 'apis/CustomTestApi';

// Accepts either a `useRef<DialogHandle>(null)` or `useRef<DialogHandle>()` ref.
type DialogRef = { current?: DialogHandle | null };

type ArchiveActionArgs = {
    onSuccess?: () => void;
    successMessage: string;
    testId: number;
};

/**
 * Shared archive/restore confirmation dialogs for custom tests. Both the lookup and
 * details pages toggle a test's active state through the exact same modal + API call;
 * only the success message and follow-up (refresh list / navigate / update state) differ,
 * which are supplied per call site.
 */
export const useCustomTestArchiveDialogs = (dialogRef: DialogRef) => {

    const { formatMessage } = useIntl();
    const { createToast } = useNotification();

    const toggleActive = async (testId: number, successMessage: string, failureMessage: string, onSuccess?: () => void) => {

        try {

            // The proc reports handled failures via Done=false in a 200 response; ApiCaller does not throw for those.
            const result = await CustomTestApi.ToggleActive(testId);

            if (!result?.Done) {

                createToast(result?.Message || failureMessage, 'danger');
                return;

            }

            createToast(successMessage, 'success', 5000);
            onSuccess?.();

        } catch (e: any) {

            createToast(e?.message ?? failureMessage, 'danger');

        }

    };

    const confirmArchive = ({ onSuccess, successMessage, testId }: ArchiveActionArgs) => {

        const dialog = dialogRef.current;
        if (!dialog) return;

        dialog.show({
            actions: [
                dialog.CancelAction({ label: formatMessage({ id: 'cancel.89a86adc', defaultMessage: 'Cancel' }) }),
                dialog.Action({
                    label: formatMessage({ id: 'archive.61ce1f3a', defaultMessage: 'Archive' }),
                    onClick: () => toggleActive(testId, successMessage, formatMessage({ id: 'failed_to_archive_test.21c8d868', defaultMessage: 'Failed to archive test.' }), onSuccess),
                    variant: 'warning'
                })
            ],
            body: (
                <div className="text-center py-2">
                    <div
                        className="d-flex align-items-center justify-content-center rounded-circle bg-warning text-white mx-auto mb-3"
                        style={{ height: 48, width: 48 }}
                    >
                        <i className="fa-solid fa-exclamation" />
                    </div>
                    <h5 className="fw-bold mb-2">
                        <FormattedMessage defaultMessage="Archive this test?" id="archive_this_test.b7fccfa7" />
                    </h5>
                    <p className="text-muted mb-0">
                        <FormattedMessage defaultMessage="This test will move out of your active list, but it won’t be deleted. You can unarchive it any time." id="this_test_will_move_out_of_your_active_l.49a12251" />
                    </p>
                </div>
            ),
            centered: true,
            size: 'md',
            title: formatMessage({ id: 'archive_test.dee94c68', defaultMessage: 'Archive Test' })
        });

    };

    const confirmRestore = ({ onSuccess, successMessage, testId }: ArchiveActionArgs) => {

        const dialog = dialogRef.current;
        if (!dialog) return;

        dialog.show({
            actions: [
                dialog.CancelAction({ label: formatMessage({ id: 'cancel.89a86adc', defaultMessage: 'Cancel' }) }),
                dialog.Action({
                    label: formatMessage({ id: 'restore.d528bbad', defaultMessage: 'Restore' }),
                    onClick: () => toggleActive(testId, successMessage, formatMessage({ id: 'failed_to_restore_test.66c19d4f', defaultMessage: 'Failed to restore test.' }), onSuccess),
                    variant: 'primary'
                })
            ],
            body: (
                <div className="text-center py-2">
                    <div
                        className="d-flex align-items-center justify-content-center rounded-circle bg-primary text-white mx-auto mb-3"
                        style={{ height: 48, width: 48 }}
                    >
                        <i className="fa-solid fa-rotate-left" />
                    </div>
                    <h5 className="fw-bold mb-2">
                        <FormattedMessage defaultMessage="Restore this test?" id="restore_this_test.e0e76d07" />
                    </h5>
                    <p className="text-muted mb-0">
                        <FormattedMessage defaultMessage="This test will move back into your active list. You can archive it again any time." id="this_test_will_move_back_into_your_activ.f84f58d8" />
                    </p>
                </div>
            ),
            centered: true,
            size: 'md',
            title: formatMessage({ id: 'restore_test.a42f5a3a', defaultMessage: 'Restore Test' })
        });

    };

    return { confirmArchive, confirmRestore };

};
